import React, { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btn, btnGold, btnGreen, btnRed, Chip, Empty, ErrorBox, field, Loading, PENALTY_STATUS_CHIP, PENALTY_TYPE_CHIP, useLoad, when } from "@/components/nds/windows/finance/parts";

function IssueForm({ residentId, onDone, onCancel }) {
  const [f, setF] = useState({ penalty_type: "demerit", amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await finApi.post("/penalties", { ...f, resident_id: residentId, amount: Number(f.amount) }); toast.success("Penalty issued · posted to ledger"); onDone(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="fin-penalty-form" className="p-4 border border-nex-gold/30 rounded-sm bg-white/[0.02] flex flex-wrap gap-2 items-center">
      <select value={f.penalty_type} onChange={set("penalty_type")} data-testid="fin-penalty-form-type" className={field}>
        {Object.entries(PENALTY_TYPE_CHIP).map(([k, [, l]]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <input type="number" min="0.01" step="0.01" required value={f.amount} onChange={set("amount")} placeholder="Amount" data-testid="fin-penalty-form-amount" className={`${field} w-32`} />
      <input required value={f.reason} onChange={set("reason")} placeholder="Reason" data-testid="fin-penalty-form-reason" className={`${field} flex-1 min-w-[180px]`} />
      <button type="button" onClick={onCancel} data-testid="fin-penalty-form-cancel" className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Cancel</button>
      <button type="submit" disabled={busy || !f.amount || !f.reason.trim()} data-testid="fin-penalty-form-submit" className={btnGold}>Issue</button>
    </form>
  );
}

function PenaltyRow({ pen, isSuper, onChanged }) {
  const [mode, setMode] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (path, body, msg) => {
    setBusy(true);
    try { await finApi.patch(`/penalties/${pen.id}/${path}`, body); toast.success(msg); setMode(null); onChanged(); }
    catch (e) { toast.error(finErr(e)); } finally { setBusy(false); }
  };
  const [tTone, tLabel] = PENALTY_TYPE_CHIP[pen.penalty_type];
  const [sTone, sLabel] = PENALTY_STATUS_CHIP[pen.status];
  return (
    <li data-testid={`fin-penalty-${pen.id}`} className="p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <Chip tone={tTone} data-testid={`fin-penalty-type-${pen.id}`}>{tLabel}</Chip>
        <Chip tone={sTone} data-testid={`fin-penalty-status-${pen.id}`}>{sLabel}</Chip>
        <span className="text-sm text-nex-silver truncate max-w-[300px]">{pen.reason}</span>
        <span data-testid={`fin-penalty-amount-${pen.id}`} className="ml-auto font-mono text-sm text-red-300">{money(pen.amount)}</span>
      </div>
      <div className="text-[11px] text-nex-muted">
        Issued {when(pen.issued_at)} by {pen.issued_by?.name || "—"}
        {pen.reviewed_at && <> · {sLabel} {when(pen.reviewed_at)} by {pen.reviewed_by?.name}</>}
        {pen.review_notes && <> · <span className="text-nex-silver/80">{pen.review_notes}</span></>}
      </div>
      {pen.status === "issued" && (!mode ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} onClick={() => act("pay", {}, "Marked paid")} data-testid={`fin-penalty-pay-${pen.id}`} className={btnGreen}>Mark paid</button>
          <button type="button" onClick={() => setMode("waive")} data-testid={`fin-penalty-waive-${pen.id}`} className={`${btn} border-sky-400/40 text-sky-300 hover:bg-sky-500/10`}>Waive</button>
          {isSuper && <button type="button" onClick={() => setMode("void")} data-testid={`fin-penalty-void-${pen.id}`} className={btnRed}>Void</button>}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={`Why ${mode} this penalty? (required)`} data-testid={`fin-penalty-notes-${pen.id}`} className={`${field} flex-1 min-w-[200px] !py-1.5`} />
          <button type="button" disabled={busy || !notes.trim()} onClick={() => act(mode, { review_notes: notes }, mode === "waive" ? "Penalty waived" : "Penalty voided")}
            data-testid={`fin-penalty-confirm-${pen.id}`} className={mode === "waive" ? `${btn} border-sky-400/40 text-sky-300` : btnRed}>Confirm {mode}</button>
          <button type="button" onClick={() => setMode(null)} data-testid={`fin-penalty-cancel-${pen.id}`} className="font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
        </div>
      ))}
    </li>
  );
}

export default function PenaltiesTab({ resident }) {
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [state, load] = useLoad(() => finApi.get("/penalties", { resident_id: resident.id }), [resident.id]);
  return (
    <div data-testid="fin-penalties-tab" className="p-5 space-y-3">
      {!adding ? (
        <button type="button" onClick={() => setAdding(true)} data-testid="fin-penalty-new" className={`${btnGold} inline-flex items-center gap-1.5`}><Plus className="h-3 w-3" /> Issue penalty</button>
      ) : <IssueForm residentId={resident.id} onCancel={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      {state.loading && !state.data ? <Loading testid="fin-penalties-list-loading" />
        : state.error ? <ErrorBox testid="fin-penalties-list-error" error={state.error} onRetry={load} />
        : state.data.penalties.length ? (
          <ul data-testid="fin-penalties-records" className="border border-white/10 rounded-sm divide-y divide-white/5">
            {state.data.penalties.map((p) => <PenaltyRow key={p.id} pen={p} isSuper={user?.role === "super_admin" || user?.is_super_admin} onChanged={load} />)}
          </ul>
        ) : <Empty testid="fin-penalties-records-empty">No penalties on record.</Empty>}
    </div>
  );
}
